import { useState } from "react";
import moment from "moment-timezone";
import { formatDateToString } from "../../../components/formatted/FormattedDateAndTime";
import { downloadFile } from "../../../shared/utils/utils";
import {
  getContainerClientRegisteredInfo,
  getHistoryCycleContainer,
} from "../../../services/container";
import { resolveFluxFromCycleAndRegistration } from "../utils/collectionExportFlux";

export const exportActivityRowToCSV = async (
  csvContent,
  container,
  activeWasteCategory,
  t,
) => {
  const currentDate = new Date(container._time);
  const content = csvContent
    .map((row) =>
      row
        .map((cell) => {
          if (
            typeof cell === "string" &&
            (cell.includes(";") || cell.includes("\n"))
          ) {
            return `"${cell}"`;
          }
          return cell;
        })
        .join(";"),
    )
    .join("\n");

  const csvWithBOM = "\uFEFF" + content;

  const blob = new Blob([csvWithBOM], { type: "text/csv;charset=utf-8;" });
  const fileName = `${t("Collection")}_${formatDateToString(currentDate)}_${container?.addressJoin?.city}_${
    activeWasteCategory !== "" ? activeWasteCategory : container.wasteName
  }.csv`;
  await downloadFile(blob, fileName);
};
/**
 * Hook pour gérer l'export CSV des collections
 */
export const useCollectionExport = (
  dataContainerCollection,
  superContainers,
  periodHistoryStart,
  periodHistoryStop,
  activeWasteCategory,
  t,
) => {
  const [loadScreen, setLoadScreen] = useState(false);
  const [progress, setProgress] = useState(0);
  const [isProgress, setIsProgress] = useState(false);

  const materialLabel = (
    JSON.parse(localStorage.getItem("materialsDescription")) || []
  )
    .sort((a, b) => a?.referenceName?.localeCompare(b?.referenceName))
    .map((item) => t(item.referenceName));

  const weightToTonnes = (weightKg, weightUnit) => {
    if (weightUnit === "t") {
      return weightKg / 1000;
    } else {
      return weightKg;
    }
  };

  const getSuperContainerId = (containerId) => {
    if (!Array.isArray(superContainers) || superContainers.length === 0)
      return null;
    const sc = superContainers.find((s) =>
      s.containerObjectIdList?.includes(containerId),
    );
    return sc ? sc._id : null;
  };

  const buildLogicalCollects = () => {
    const groups = new Map();
    for (const item of dataContainerCollection) {
      const scId = getSuperContainerId(item._measurement ?? item._id);
      const key = `${item._time}_${scId ?? item._measurement ?? item._id}`;
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(item);
    }
    for (const arr of groups.values()) {
      arr.sort((a, b) => {
        const aLeft = a._value_image?.includes?.("-L.jpeg") ? 0 : 1;
        const bLeft = b._value_image?.includes?.("-L.jpeg") ? 0 : 1;
        return aLeft - bLeft;
      });
    }
    return Array.from(groups.values());
  };

  const fetchMergedCycleData = async (leftItem, rightItem, index, total) => {
    const [cycleLeft, cycleRight] = await Promise.all([
      getHistoryCycleContainer(
        {
          currentCycle: false,
          getLevelLastRotation: false,
          containerCollectionDate: leftItem._time,
        },
        leftItem._measurement,
      ),
      getHistoryCycleContainer(
        {
          currentCycle: false,
          getLevelLastRotation: false,
          containerCollectionDate: rightItem._time,
        },
        rightItem._measurement,
      ),
    ]);

    const superContainerId = getSuperContainerId(leftItem._measurement);
    const dropOff =
      cycleLeft?.data?.historyCycle?.[cycleLeft.data.historyCycle.length - 1]
        ?._time;

    const clientRegisteredInfo = await getContainerClientRegisteredInfo(
      {
        start: new Date(new Date(dropOff).getTime() - 1).toISOString(),
        stop: new Date(new Date(leftItem._time).getTime() + 1).toISOString(),
      },
      superContainerId ?? leftItem._measurement,
    );

    const clientName = clientRegisteredInfo?.data?.client
      ? JSON.parse(clientRegisteredInfo?.data?.client).name
      : "";
    const clientCode = clientRegisteredInfo?.data?.client
      ? JSON.parse(clientRegisteredInfo?.data?.client).code
      : "";
    const clientRegisteredWeight = clientRegisteredInfo?.data?.weight
      ? clientRegisteredInfo?.data?.weight / 1000
      : "";

    const dataLeft = cycleLeft?.status === 200 ? cycleLeft.data : {};
    const dataRight = cycleRight?.status === 200 ? cycleRight.data : {};

    const totalVolumeSortingError =
      (dataLeft.totalVolumeSortingError || 0) +
      (dataRight.totalVolumeSortingError || 0);
    const totalWasteVolume =
      (dataLeft.totalWasteVolume || 0) + (dataRight.totalWasteVolume || 0);
    const mergedAnomalyDelivery = { ...dataLeft.anomalyDelivery };
    for (const [key, value] of Object.entries(
      dataRight.anomalyDelivery || {},
    )) {
      mergedAnomalyDelivery[key] = (mergedAnomalyDelivery[key] || 0) + value;
    }

    const formattedAnomalies = Object.entries(mergedAnomalyDelivery).reduce(
      (acc, [key, value]) => {
        acc[t(key)] = value;
        return acc;
      },
      {},
    );
    const anomaliesData = materialLabel.map((element) => {
      const key = element.replace(/Anomalie |Anomaly /, "");
      return formattedAnomalies[key] !== undefined
        ? formattedAnomalies[key]
        : 0;
    });

    const contaminants =
      totalVolumeSortingError > 0 && totalWasteVolume > 0
        ? (totalVolumeSortingError * 100) / totalWasteVolume < 1
          ? "<1"
          : Math.round((totalVolumeSortingError * 100) / totalWasteVolume)
        : 0;

    const numberOfPackmat =
      (dataLeft.numberOfPackmat || 0) + (dataRight.numberOfPackmat || 0);
    const fluxPerCollect = resolveFluxFromCycleAndRegistration(
      leftItem,
      dataLeft?.historyCycle,
      clientRegisteredInfo?.data?.waste_category,
    );

    const newProgress = ((index + 1) / total) * 100;
    setProgress(newProgress);

    const item = leftItem;
    return [
      `${moment(dropOff).tz("Europe/Paris").format("DD-MM-YYYY HH:mm")}`,
      `${moment(item._time).tz("Europe/Paris").format("DD-MM-YYYY HH:mm")}`,
      clientName,
      clientCode,
      `${item.addressName}${item?.positioning ? " | " + item.positioning : ""}`,
      fluxPerCollect,
      dataLeft?.historyCycle?.length > 1
        ? `${dataLeft?.historyCycle?.[0]?.filling_level ?? 0}%`
        : "",
      clientRegisteredWeight,
      numberOfPackmat,
      `${contaminants}%`,
      (dataLeft.numberOfAnomaly || 0) + (dataRight.numberOfAnomaly || 0),
      ...anomaliesData,
    ];
  };

  const fetchCycleData = async (item, index, totalExportRows) => {
    let cycleData = {};
    let id = item._measurement;
    if (superContainers && superContainers.length > 0) {
      superContainers.forEach((superContainer) => {
        if (superContainer.containerObjectIdList.includes(id)) {
          id = superContainer._id;
        }
      });
    }
    const responseHistoryCycle = await getHistoryCycleContainer(
      {
        currentCycle: false,
        getLevelLastRotation: false,
        containerCollectionDate: item._time,
      },
      item._measurement,
    );

    const dropOff =
      responseHistoryCycle?.data?.historyCycle[
        responseHistoryCycle.data.historyCycle.length - 1
      ]?._time;

    const clientRegisteredInfo = await getContainerClientRegisteredInfo(
      {
        start: new Date(new Date(dropOff).getTime() - 1).toISOString(),
        stop: new Date(new Date(item._time).getTime() + 1).toISOString(),
      },
      id,
    );

    if (responseHistoryCycle.status === 200) {
      cycleData = responseHistoryCycle.data;
    }

    const clientName = clientRegisteredInfo?.data?.client
      ? JSON.parse(clientRegisteredInfo?.data?.client).name
      : "";

    const clientCode = clientRegisteredInfo?.data?.client
      ? JSON.parse(clientRegisteredInfo?.data?.client).code
      : "";

    const clientRegisteredWeight = clientRegisteredInfo?.data?.weight
      ? clientRegisteredInfo?.data?.weight / 1000
      : cycleData?.historyCycle.length > 1
        ? (cycleData.historyCycle[0].mass / 1000).toFixed(2)
        : "";

    const newProgress = ((index + 1) / totalExportRows) * 100;
    setProgress(newProgress);

    const formattedAnomalies = Object.entries(cycleData.anomalyDelivery).reduce(
      (acc, [key, value]) => {
        acc[t(key)] = value;
        return acc;
      },
      {},
    );

    const anomaliesData = materialLabel.map((element) => {
      const key = element.replace(/Anomalie |Anomaly /, "");
      return formattedAnomalies[key] !== undefined
        ? formattedAnomalies[key]
        : 0;
    });

    const contaminants =
      cycleData.totalVolumeSortingError > 0
        ? (cycleData.totalVolumeSortingError * 100) /
            cycleData.totalWasteVolume <
          1
          ? "<1"
          : Math.round(
              (cycleData.totalVolumeSortingError * 100) /
                cycleData.totalWasteVolume,
            )
        : 0;

    const fluxPerCollect = resolveFluxFromCycleAndRegistration(
      item,
      cycleData?.historyCycle,
      clientRegisteredInfo?.data?.waste_category,
    );

    return [
      `${moment(dropOff).tz("Europe/Paris").format("DD-MM-YYYY HH:mm")}`,
      `${moment(item._time).tz("Europe/Paris").format("DD-MM-YYYY HH:mm")}`,
      clientName,
      clientCode,
      `${item.addressName}${item?.positioning ? " | " + item.positioning : ""}`,
      fluxPerCollect,
      cycleData?.historyCycle.length > 1
        ? `${cycleData?.historyCycle[0]?.filling_level || 0}%`
        : "",
      clientRegisteredWeight,
      cycleData.numberOfPackmat,
      `${contaminants}%`,
      cycleData.numberOfAnomaly || 0,
      ...anomaliesData,
    ];
  };

  const exportCollectionToCSV = async () => {
    setLoadScreen(true);
    setIsProgress(true);

    const label = [
      t("Drop-off date"),
      t("Pickup date"),
      t("Client"),
      t("Reference"),
      t("Address"),
      t("Stream"),
      t("Filling level"),
      t("Weight (tons)"),
      t("Compaction"),
      t("% of undesirables"),
      t("Number of sorting errors"),
      ...materialLabel,
    ];

    const logicalCollects = buildLogicalCollects();
    const lastData = [];
    for (let i = 0; i < logicalCollects.length; i++) {
      const group = logicalCollects[i];
      if (group.length === 2) {
        const row = await fetchMergedCycleData(
          group[0],
          group[1],
          i,
          logicalCollects.length,
        );
        lastData.push(row);
      } else {
        const row = await fetchCycleData(group[0], i, logicalCollects.length);
        lastData.push(row);
      }
    }

    const csvContent = [label, ...lastData];

    const content = csvContent
      .map((row) =>
        row
          .map((cell) => {
            if (
              typeof cell === "string" &&
              (cell.includes(";") || cell.includes("\n"))
            ) {
              return `"${cell}"`;
            }
            return cell;
          })
          .join(";"),
      )
      .join("\n");

    const csvWithBOM = "\uFEFF" + content;

    const blob = new Blob([csvWithBOM], { type: "text/csv;charset=utf-8;" });
    const fileName = `${t("Collection")}_${t("from")}_${moment(periodHistoryStart).tz("Europe/Paris").format("DD-MM-YYYY_HH-mm")}_${t("to")}_${moment(periodHistoryStop).tz("Europe/Paris").format("DD-MM-YYYY_HH-mm")}.csv`;
    await downloadFile(blob, fileName);
    setProgress(0);
    setLoadScreen(false);
    setIsProgress(false);
  };

  const handleCsvExport = async (
    historyCurrent,
    timeLine,
    client,
    reference,
    weight,
  ) => {
    setIsProgress(true);
    setLoadScreen(true);
    const showLevel = historyCurrent.length > 1;

    const label = [
      t("Date"),
      t("Client"),
      t("Reference"),
      ...(showLevel ? [t("Filling level")] : []),
      t("Weight (tons)"),
    ];
    const lastData = historyCurrent.map((item, index) => [
      moment(item._time).tz("Europe/Paris").format("DD MM YYYY HH:mm"),
      client,
      reference,
      ...(showLevel ? [`${item.filling_level}%`] : []),
      index === 0 && weight && weight !== 0
        ? weight
        : historyCurrent.length > 1
          ? (item.mass / 1000).toFixed(2)
          : "",
    ]);
    const csvContent = [label, ...lastData];
    exportDataToCSV(csvContent, timeLine);
  };

  const exportDataToCSV = async (csvContent, container) => {
    await exportActivityRowToCSV(csvContent, container, activeWasteCategory, t);
    setLoadScreen(false);
    setIsProgress(false);
  };

  return {
    loadScreen,
    setLoadScreen,
    progress,
    isProgress,
    setIsProgress,
    exportCollectionToCSV,
    handleCsvExport,
    weightToTonnes,
  };
};
